import React from "react";
// Common Components
import {
  DynamicDropdown,
  Button,
  Modal,
  FullScreenLoading,
  FileDownloader,
} from "src/Components";
import SelectableButtons from "src/Components/Dropdown/SelectableButtons";
// Local Components
import GoldTable from "./GoldTable";
import UserPositionSummaryTable from "./UserPositionSummaryTable";
import PortfoAssetValueTable from "./PortfoAssetValueTable";
import UserBaseDetailTable from "./UserBaseDetailTable";
import UserFutureDetail from "./UserFutureDetail";
import UserCallOptionDetail from "./UserCallOptionDetail";
import BasketPercentageBasedOnAssets from "./BasketPercentageBasedOnAssets";
import PureAssetValue from "./PureAssetValue";
import MarginStatus from "./MarginStatus";
import GoldCertificateExposure from "./GoldCertificateExposure";
import AccountBalance from "./AccountBalance";
import SettingModal from "./Setting";
import Payment from "./Payment";
import Investment from "./Investment";
import UserExcludedCalculationSymbolsTable from "./UserExcludedCalculationSymbolsTable";
// Hooks
// Services
import {
  portfoForceUpdate,
  getWalletUsers,
  getUserBaseAssetDropdown,
  getPortfoExcelFile,
  portfoForceUserUpdate,
} from "src/Requests/imePortfolio";
// Utilities
import { packetErrorHandler } from "src/Utils/requestUtils";
import { toast } from "react-toastify";
// Types
import {
  IUserPositionSummarySearchParams,
  ITable2SearchParams,
} from "src/Interfaces/imePortfolio";
import { DropdownItem } from "src/Interfaces/dropdownInterfaces";
import { isCustomerUser } from "src/helper";

enum ModalMode {
  Closed,
  Setting,
  Details,
}
const InsTableWrapper = () => {
  const isCustomer = isCustomerUser();
  // state
  const [loading, setLoading] = React.useState(false);
  const [modal, setModal] = React.useState<ModalMode>(ModalMode.Closed);
  const [refreshFlag, setRefresh] = React.useState(false);
  const [totalRefreshFlag, setTotalRefreshFlag] = React.useState(false);
  const [userIdSearchParams, setUserIdSearchParams] =
    React.useState<IUserPositionSummarySearchParams>(
      {} as IUserPositionSummarySearchParams,
    );
  const [searchParams, setSearchParams] = React.useState<ITable2SearchParams>(
    {} as ITable2SearchParams,
  );
  const [baseAssets, setBaseAssets] = React.useState([] as DropdownItem[]);

  React.useEffect(() => {
    if (isCustomer) {
      getUserBaseAssetDropdown()
        .then((resp) => {
          setBaseAssets(resp);
        })
        .catch(packetErrorHandler);
      return;
    }

    if (userIdSearchParams.userId) {
      getUserBaseAssetDropdown(userIdSearchParams.userId)
        .then((resp) => {
          setBaseAssets(resp);
        })
        .catch(packetErrorHandler);
    } else {
      setBaseAssets([] as DropdownItem[]);
    }
  }, [userIdSearchParams.userId, isCustomer]);

  React.useEffect(() => {
    if (refreshFlag) {
      setRefresh(false);
    }
  }, [refreshFlag]);

  React.useEffect(() => {
    if (totalRefreshFlag) {
      setTotalRefreshFlag(false);
    }
  }, [totalRefreshFlag]);

  const forceUpdateHandler = () => {
    if (loading) return;
    setLoading(true);
    portfoForceUpdate()
      .then((res) => {
        if (!res.data) toast.error(res.msg);
        else {
          toast.success(res.msg);
          setTotalRefreshFlag(true);
        }
      })
      .catch(packetErrorHandler)
      .finally(() => setLoading(false));
  };

  const forceUserUpdateHandler = () => {
    if (loading) return;
    setLoading(true);
    portfoForceUserUpdate(userIdSearchParams.userId)
      .then((res) => {
        if (!res.data) toast.error(res.msg);
        else {
          toast.success(res.msg);
          setTotalRefreshFlag(true);
        }
      })
      .catch(packetErrorHandler)
      .finally(() => setLoading(false));
  };

  const getModalUI = React.useMemo(() => {
    switch (modal) {
      case ModalMode.Setting:
        return <SettingModal />;
    }
  }, [modal]);

  return (
    <div className="flex flex-col justify-start items-start gap-2 w-full px-8">
      {loading ? <FullScreenLoading /> : <></>}
      <div className="flex justify-start items-start gap-4">
        {!isCustomer && (
          <div className="w-min">
            <Button
              onClick={() => setModal(ModalMode.Setting)}
              text="تنظیمات"
              variant="contained"
            />
          </div>
        )}
        <div className="w-min">
          <Button
            onClick={forceUpdateHandler}
            text="دریافت اطلاعات"
            variant="contained"
          />
        </div>
        <FileDownloader getFile={getPortfoExcelFile} />
      </div>
      <div className="flex flex-row justify-start items-end gap-3 w-full min-w-[1200px]">
        {modal ? (
          <Modal title="تنظیمات" UI={getModalUI} onClose={() => setModal(ModalMode.Closed)} />
        ) : (
          <></>
        )}

        {!isCustomer && (
          <div className="w-[250px]">
            <DynamicDropdown
              dropdown={{
                name: "userId",
                placeholder: "انتخاب کاربران",
                extraLabel: true,
              }}
              getData={getWalletUsers}
              onChange={(_, value) => {
                if (value) {
                  setUserIdSearchParams({ userId: value });
                  setSearchParams({ userId: value } as ITable2SearchParams);
                } else {
                  setUserIdSearchParams({} as IUserPositionSummarySearchParams);
                  setSearchParams({} as ITable2SearchParams);
                }
              }}
              value={userIdSearchParams.userId}
            />
          </div>
        )}
        <SelectableButtons
          label="دارایی پایه"
          name="instrumentTicker"
          options={baseAssets}
          hideSelectAll
          onChange={(_, value) =>
            setSearchParams({
              ...searchParams,
              // userId: userIdSearchParams.userId,
              instrumentTicker: value,
            })
          }
          value={searchParams.instrumentTicker?.toString()}
        />
      </div>
      {/* <PortfoAssetValueTable searchParams={userIdSearchParams} /> */}
      <div className="flex justify-center align-center">
        <Investment
          searchParams={userIdSearchParams}
          refreshFlag={refreshFlag}
          setRefresh={setRefresh}
          totalRefreshFlag={totalRefreshFlag}
        />
        <AccountBalance
          searchParams={userIdSearchParams}
          totalRefreshFlag={totalRefreshFlag}
        />
        <PureAssetValue
          searchParams={userIdSearchParams}
          totalRefreshFlag={totalRefreshFlag}
        />
        <MarginStatus
          searchParams={userIdSearchParams}
          totalRefreshFlag={totalRefreshFlag}
        />
        <GoldCertificateExposure
          searchParams={userIdSearchParams}
          totalRefreshFlag={totalRefreshFlag}
        />
        <BasketPercentageBasedOnAssets
          searchParams={userIdSearchParams}
          totalRefreshFlag={totalRefreshFlag}
        />
        <Payment
          searchParams={userIdSearchParams}
          refreshFlag={refreshFlag}
          setRefresh={setRefresh}
          totalRefreshFlag={totalRefreshFlag}
        />
      </div>

      <div className="w-full flex flex-row justify-start items-start gap-1">
        <div className="w-2/3 flex flex-col gap-1">
          <div className="flex gap-1">
            <UserPositionSummaryTable
              searchParams={searchParams}
              totalRefreshFlag={totalRefreshFlag}
            />
            <UserBaseDetailTable
              searchParams={searchParams}
              totalRefreshFlag={totalRefreshFlag}
            />
          </div>
          <div className="flex gap-1">
            <GoldTable
              searchParams={searchParams}
              totalRefreshFlag={totalRefreshFlag}
            />
            <UserFutureDetail
              searchParams={searchParams}
              totalRefreshFlag={totalRefreshFlag}
            />
          </div>
          <div className="w-1/2">
            <UserExcludedCalculationSymbolsTable
              searchParams={userIdSearchParams}
              totalRefreshFlag={totalRefreshFlag}
              forceUpdateHandler={forceUserUpdateHandler}
            />
          </div>
        </div>
        <div className="w-full">
          <UserCallOptionDetail
            searchParams={searchParams}
            totalRefreshFlag={totalRefreshFlag}
          />
        </div>
      </div>
    </div>
  );
};

export default InsTableWrapper;
